import type { TableElement, MergedCell, TableCell } from '../model/form.types';

interface CellPosition {
  row: number;
  col: number;
}

interface Bounds {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}

function computeBounds(cells: CellPosition[]): Bounds {
  const bounds: Bounds = {
    minRow: cells[0].row,
    maxRow: cells[0].row,
    minCol: cells[0].col,
    maxCol: cells[0].col,
  };
  for (const { row, col } of cells) {
    if (row < bounds.minRow) bounds.minRow = row;
    if (row > bounds.maxRow) bounds.maxRow = row;
    if (col < bounds.minCol) bounds.minCol = col;
    if (col > bounds.maxCol) bounds.maxCol = col;
  }
  return bounds;
}

function regionOverlapsBounds(region: MergedCell, bounds: Bounds): boolean {
  return (
    region.startRow <= bounds.maxRow &&
    region.endRow >= bounds.minRow &&
    region.startCol <= bounds.maxCol &&
    region.endCol >= bounds.minCol
  );
}

function absorbRegion(region: MergedCell, bounds: Bounds): boolean {
  let grew = false;
  if (region.startRow < bounds.minRow) {
    bounds.minRow = region.startRow;
    grew = true;
  }
  if (region.endRow > bounds.maxRow) {
    bounds.maxRow = region.endRow;
    grew = true;
  }
  if (region.startCol < bounds.minCol) {
    bounds.minCol = region.startCol;
    grew = true;
  }
  if (region.endCol > bounds.maxCol) {
    bounds.maxCol = region.endCol;
    grew = true;
  }
  return grew;
}

// Grow the box until it fully encloses every merge region it overlaps. We scan
// the (small) mergedCells list rather than the grid, and drop each region once
// absorbed so it is never re-tested. A region that grows the box can pull a
// previously-disjoint region into range, hence the outer loop until stable.
function growBoundsOverMergeRegions(bounds: Bounds, table: TableElement): void {
  const pending = [...(table.properties.mergedCells ?? [])];
  let grew = true;
  while (grew && pending.length > 0) {
    grew = false;
    for (let i = pending.length - 1; i >= 0; i--) {
      const region = pending[i];
      if (!regionOverlapsBounds(region, bounds)) continue;

      pending.splice(i, 1);
      if (absorbRegion(region, bounds)) grew = true;
    }
  }
}

function clampBoundsToTable(bounds: Bounds, table: TableElement): void {
  bounds.minRow = Math.max(0, bounds.minRow);
  bounds.minCol = Math.max(0, bounds.minCol);
  bounds.maxRow = Math.min(table.properties.rows - 1, bounds.maxRow);
  bounds.maxCol = Math.min(table.properties.columns - 1, bounds.maxCol);
}

function boundsToCells(bounds: Bounds): CellPosition[] {
  const cells: CellPosition[] = [];
  for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
    for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
      cells.push({ row, col });
    }
  }
  return cells;
}

export function getFirstNonEmptyCell(
  cells: TableCell[][],
  bounds: Bounds
): TableCell | undefined {
  for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
    for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
      const cell = cells[row]?.[col];
      if (cell?.content !== undefined) return cell;
    }
  }
  return undefined;
}

export function isRectangularSelection(cells: CellPosition[]): boolean {
  if (cells.length === 0) return false;
  if (cells.length === 1) return true;

  const rows = cells.map((c) => c.row);
  const cols = cells.map((c) => c.col);

  const minRow = Math.min(...rows);
  const maxRow = Math.max(...rows);
  const minCol = Math.min(...cols);
  const maxCol = Math.max(...cols);

  const expectedCells = (maxRow - minRow + 1) * (maxCol - minCol + 1);

  if (cells.length !== expectedCells) {
    return false;
  }

  for (let row = minRow; row <= maxRow; row++) {
    for (let col = minCol; col <= maxCol; col++) {
      const hasCell = cells.some((c) => c.row === row && c.col === col);
      if (!hasCell) {
        return false;
      }
    }
  }

  return true;
}

function getCellIds(cells: CellPosition[], table: TableElement): Set<string> {
  return new Set(
    cells
      .map((c) => table.properties.cells[c.row]?.[c.col]?.id)
      .filter((id): id is string => id !== undefined)
  );
}

function getMergeGroupsInSelection(cells: CellPosition[], table: TableElement): Set<string> {
  const mergeGroups = new Set<string>();

  for (const cellPos of cells) {
    const cell = table.properties.cells[cellPos.row]?.[cellPos.col];
    if (cell?.mergeGroupId) {
      mergeGroups.add(cell.mergeGroupId);
    }
  }

  return mergeGroups;
}

function isCompleteMergeGroupSelected(
  cells: CellPosition[],
  mergeGroupId: string,
  table: TableElement
): boolean {
  const mergedCells = table.properties.mergedCells ?? [];
  const mergeGroup = mergedCells.find((m: MergedCell) => m.id === mergeGroupId);

  if (!mergeGroup) return false;

  const cellIds = getCellIds(cells, table);
  const allSelectedBelongToGroup = cells.every((c) => {
    const cell = table.properties.cells[c.row]?.[c.col];
    return cell?.mergeGroupId === mergeGroupId;
  });

  const allGroupCellsSelected = mergeGroup.cells.every((cellId: string) => cellIds.has(cellId));

  return allSelectedBelongToGroup && allGroupCellsSelected;
}

function validatePartialMergeGroups(
  cells: CellPosition[],
  mergeGroupIds: Set<string>,
  table: TableElement
): { valid: boolean; reason?: string } {
  const cellIds = getCellIds(cells, table);
  const mergedCells = table.properties.mergedCells ?? [];

  for (const mergeGroupId of mergeGroupIds) {
    const mergeGroup = mergedCells.find((m: MergedCell) => m.id === mergeGroupId);
    if (!mergeGroup) continue;

    const allMergedCellsInSelection = mergeGroup.cells.every((cellId: string) =>
      cellIds.has(cellId)
    );

    if (!allMergedCellsInSelection) {
      return {
        valid: false,
        reason:
          'A seleção contém apenas parte de um grupo mesclado. Selecione todo o grupo ou desmembre primeiro.',
      };
    }
  }

  return { valid: true };
}

export function canMergeCells(
  cells: CellPosition[],
  table: TableElement
): {
  canMerge: boolean;
  reason?: string;
} {
  if (cells.length < 2) {
    return { canMerge: false, reason: 'Selecione pelo menos 2 células para mesclar' };
  }

  if (!isRectangularSelection(cells)) {
    return { canMerge: false, reason: 'A seleção deve formar um retângulo contínuo' };
  }

  // Validate all cells exist
  for (const cellPos of cells) {
    const cell = table.properties.cells[cellPos.row]?.[cellPos.col];
    if (!cell) {
      return { canMerge: false, reason: 'Célula inválida na seleção' };
    }
  }

  const mergeGroupsInSelection = getMergeGroupsInSelection(cells, table);

  // Check if user selected a single complete merged cell (should only allow unmerge)
  if (mergeGroupsInSelection.size === 1) {
    const mergeGroupId = Array.from(mergeGroupsInSelection)[0];
    if (isCompleteMergeGroupSelected(cells, mergeGroupId, table)) {
      return { canMerge: false };
    }
  }

  // Validate that all merge groups are completely selected (no partial selections)
  if (mergeGroupsInSelection.size > 0) {
    const validation = validatePartialMergeGroups(cells, mergeGroupsInSelection, table);
    if (!validation.valid) {
      return { canMerge: false, reason: validation.reason };
    }
  }

  return { canMerge: true };
}

export function canUnmergeCells(
  mergeGroupId: string | undefined,
  table: TableElement
): {
  canUnmerge: boolean;
  reason?: string;
} {
  if (!mergeGroupId) {
    return { canUnmerge: false, reason: 'Célula não está mesclada' };
  }

  const mergedCells = table.properties.mergedCells ?? [];
  const mergedCell = mergedCells.find((m: MergedCell) => m.id === mergeGroupId);

  if (!mergedCell) {
    return { canUnmerge: false, reason: 'Grupo de mesclagem não encontrado' };
  }

  return { canUnmerge: true };
}

export function getMergedCellBounds(
  mergeGroupId: string,
  table: TableElement
): {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
} | null {
  const mergedCells = table.properties.mergedCells ?? [];
  const mergedCell = mergedCells.find((m: MergedCell) => m.id === mergeGroupId);

  if (!mergedCell) return null;

  return {
    startRow: mergedCell.startRow,
    startCol: mergedCell.startCol,
    endRow: mergedCell.endRow,
    endCol: mergedCell.endCol,
  };
}

export function expandRangeToMergeAwareRectangle(
  cells: CellPosition[],
  table: TableElement
): CellPosition[] {
  if (cells.length === 0) return cells;

  const bounds = computeBounds(cells);
  growBoundsOverMergeRegions(bounds, table);
  clampBoundsToTable(bounds, table);

  return boundsToCells(bounds);
}

export function getMergeGroupId(row: number, col: number, table: TableElement): string | undefined {
  const cell = table.properties.cells[row]?.[col];
  return cell?.mergeGroupId;
}

export function isCellMerged(row: number, col: number, table: TableElement): boolean {
  const cell = table.properties.cells[row]?.[col];
  return cell?.isMerged ?? false;
}
